import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";
import { SignalEntry } from "@/shared/domain/workspaces/signalValidation";

// Payload for opening a message path panel in the visualizer
export interface OpenMessagePathPayload {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
  timestamp?: bigint;
}

// Payload for opening topic search in the visualizer
export interface OpenTopicSearchPayload {
  topic: TopicRecord;
  timestamp?: bigint;
}

// Payload for seeking the timer to a specific timestamp
export interface SeekTimerPayload {
  timestamp: bigint;
}

/** One signal to visualize: a message path together with its parent topic. */
export type WorkspaceActionEntry = SignalEntry;

/**
 * Where to place a newly created panel, relative to an existing panel.
 * A panel's layout item shares the panel's id, so `relativeToPanelId` is
 * resolved directly against the layout tree.
 */
export interface PanelPlacementInput {
  relativeToPanelId: string;
  orientation: "top" | "right" | "bottom" | "left";
}

/**
 * Result reported back to the publisher of a panel-mutation workspace action.
 *
 * The PubSubBus invokes subscribers synchronously in the publisher's stack
 * (a documented contract on `AiAgentThreadPubSubBus.publish`), so `onResult`
 * fires before `publish()` returns. Handlers validate the whole batch and
 * only then dispatch, so a validation failure (`ok: false` with
 * `invalidPaths`) means no panel state was changed; an unexpected-error
 * failure says in its `error` text that the workspace may have been left
 * partially modified. `invalidPaths` carries per-message-path reasons so an
 * AI caller can adjust its request and retry.
 */
export type WorkspaceActionResult =
  | { ok: true; panelId: string }
  | {
      ok: false;
      error: string;
      invalidPaths?: { messagePathId: string; reason: string }[];
    };

// Payload for creating one panel holding one or more signals
export interface CreatePanelActionPayload {
  entries: WorkspaceActionEntry[];
  timestamp?: bigint;
  placement?: PanelPlacementInput;
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for adding signals to an existing panel
export interface AddToPanelActionPayload {
  panelId: string;
  entries: WorkspaceActionEntry[];
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for removing signals from an existing panel
export interface RemoveFromPanelActionPayload {
  panelId: string;
  messagePathIds: string[];
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for removing an entire panel
export interface RemovePanelActionPayload {
  panelId: string;
  onResult: (result: WorkspaceActionResult) => void;
}

/**
 * The subset of workspace actions that mutate panels and report a result
 * through a synchronous `onResult` callback. Publishers that need the
 * request/response round-trip must build one of these.
 */
export type PanelMutationActionPayload = Extract<
  WorkspaceActionPayload,
  {
    type:
      | "create_panel"
      | "add_to_panel"
      | "remove_from_panel"
      | "remove_panel";
  }
>;

// Union type for all workspace action payloads
export type WorkspaceActionPayload =
  | { type: "open_message_path"; payload: OpenMessagePathPayload }
  | { type: "open_topic_search"; payload: OpenTopicSearchPayload }
  | { type: "seek_timer"; payload: SeekTimerPayload }
  | { type: "create_panel"; payload: CreatePanelActionPayload }
  | { type: "add_to_panel"; payload: AddToPanelActionPayload }
  | { type: "remove_from_panel"; payload: RemoveFromPanelActionPayload }
  | { type: "remove_panel"; payload: RemovePanelActionPayload };

// PubSub topic names
export const WORKSPACE_ACTION_TOPIC = "workspace:action";
